import React from 'react';
import I18n from 'i18n-js';
import {
  BuyItNowPaymentOptionsFields,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '../schemas/rql';
import BuyItNowApplePayWrapper from './BuyItNowApplePayWrapper';
import ExpressPayPalButton from './checkout/ExpressPayPalButton';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import { CheckoutItemPageReturnErrorCallback } from './checkout_item_page_return_layout';
import { ExpressPaySeparator } from './ExpressPaySeparator';
import { inMobileBrowser, inWeb } from '@reverbdotcom/commons/src/user_context_helpers';
import { CheckoutPayPalBuyNowButton } from './CheckoutPayPalBuyNowButton';
import { usePayPalCurrency } from './useExpressPayPal';
import experiments from '@reverbdotcom/commons/src/experiments';
import { ListingFinancingCallout } from './ListingFinancingCallout';

type Listing = BuyItNowPaymentOptionsFields.Fragment;

interface Props {
  acceptedPaymentMethods: string[];
  adyenCheckoutPaymentMethodsConfig: string;
  onError: (errors) => void;
  onApplePayError: CheckoutItemPageReturnErrorCallback;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  listing: Listing;
  quantity?: number;
}

export default function BuyItNowExpressPayWrapper({
  acceptedPaymentMethods,
  adyenCheckoutPaymentMethodsConfig,
  onError,
  onApplePayError,
  isLoading,
  setIsLoading,
  listing,
  quantity = null,
}: Props) {
  const user = useUser();

  const paymentMethods = mapPaymentMethods(acceptedPaymentMethods);
  const showPaypalCheckoutButton = paymentMethods.includes(CheckoutPaymentMethodType.DC_VIA_PAYPAL);
  const inPayPalPayLaterExp = useExpEnabled(experiments.PAY_LATER_BUY_NOW);
  const showPaypalPayLaterButton = paymentMethods.includes(CheckoutPaymentMethodType.PAYPAL_PAY_LATER) && inPayPalPayLaterExp;
  const { payPalCurrency } = usePayPalCurrency({ fallbackCurrency: listing.currency });

  return (
    <div className="buy-it-now-express-pay-wrapper">
      <div className="mb-4">
        <div className="weight-bold mb-4 align-center">
          {I18n.t('discovery.checkout.expressCheckout.title')}
        </div>
        {inPayPalPayLaterExp &&
          <div className="listing-financing-callout">
            <ListingFinancingCallout
              listing={listing}
              showPaypalCallout
            />
          </div>
        }
        {showPaypalCheckoutButton && inMobileBrowser(user) &&
          <div className="paypal-buy-now-button">
            <ExpressPayPalButton
              currency={payPalCurrency}
              checkoutBundlingId={listingCheckoutBundlingId(listing)}
              onError={onError}
              quantity={quantity}
              amountCents={listing.pricing.buyerPrice.amountCents}
              parentComponentName="BuyItNowExpressPayWrapper"
              showPaypalPayLaterButton={showPaypalPayLaterButton}
            />
          </div>
        }
        {showPaypalCheckoutButton && inWeb(user) &&
          <>
            <CheckoutPayPalBuyNowButton
              currency={payPalCurrency}
              checkoutBundlingId={listingCheckoutBundlingId(listing)}
              onError={onError}
              quantity={quantity}
              parentComponentName="BuyItNowExpressPayWrapper"
              listingCurrency={listing.currency}
            />
            {inPayPalPayLaterExp &&
              <div className="paypal-buy-now-button--pay-later">
                <ExpressPayPalButton
                  currency={payPalCurrency}
                  checkoutBundlingId={listingCheckoutBundlingId(listing)}
                  onError={onError}
                  quantity={quantity}
                  amountCents={listing.pricing.buyerPrice.amountCents}
                  parentComponentName="BuyItNowExpressPayWrapper"
                  showPaypalPayLaterButton={showPaypalPayLaterButton}
                  payLaterOnly
                />
              </div>
            }
          </>
        }
        <div className="mt-1">
          <BuyItNowApplePayWrapper
            listingId={listing.id}
            listingCurrencyCode={listing.currency}
            paymentMethodsConfig={adyenCheckoutPaymentMethodsConfig}
            setIsLoading={setIsLoading}
            isDisabled={isLoading}
            onError={onApplePayError}
            inExpressPay
          />
        </div>
        <div className="mt-4 mb-2">
          <ExpressPaySeparator />
        </div>
      </div>
    </div>
  );
}

function mapPaymentMethods(acceptedPaymentMethods: string[]) {
  return acceptedPaymentMethods.map((paymentMethod) => paymentMethod.toUpperCase() as CheckoutPaymentMethodType);
}

function listingCheckoutBundlingId(listing: Listing) {
  return `RL-${listing.id}`;
}
